import { ApolloError } from "@apollo/client";
import { WorkflowAwardExhibit } from "@codegen/enums";
import {
  useAllUserDocumentsByTypeQuery,
  useDeleteUserDocumentMutation,
  useExhibitFilesQuery,
  useUnlinkExhibitFileMutation,
} from "@codegen/index";
import { IndividualDocumentType } from "@codegen/schema";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { UploadFileStatus } from "@components/uploader/types";
import Uploader from "@components/uploader/uploader";
import { zodResolver } from "@hookform/resolvers/zod";
import { ScreenshotFootnote } from "@pages/onboarding/shared/footnotes";
import { WaitingSplash } from "@pages/onboarding/shared/waiting";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import * as Sentry from "@sentry/react";
import { api } from "@utils/api";
import { useLogError } from "@utils/error";
import { useJwt } from "@utils/hooks";
import * as changeCase from "change-case";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export const fileFormSchema = z.object({
  description: z.string().min(1, "Required"),
  files: z.array(z.string()).min(1, "Required"),
});

export type FileFormWithDescriptionType = z.infer<typeof fileFormSchema>;
export const FileWithLinksForm: React.FC<
  OnboardingPageType & {
    fileType: IndividualDocumentType;
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
    descriptionLabel: React.ReactNode;
    userDocuments?:
      | {
          documentType: IndividualDocumentType;
          fileByFileId: { id: string; name: string } | null;
        }[]
      | null;
  }
> = ({
  contact,
  onBack,
  onSubmit,
  autoSave,
  onSkip,
  isSaving,
  fileType,
  title,
  subtitle,
  description: descriptionProp,
  footnote,
  descriptionLabel,
  exhibit,
  userDocuments,
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { required, data } = exhibit;
  const token = useJwt();
  const logError = useLogError();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const { description }: { description?: string; files?: string[] } =
    (data as FileFormWithDescriptionType) ?? {
      description: "",
    };

  const existingFiles =
    userDocuments?.map((node) => node?.fileByFileId).filter((n) => !!n) ?? [];

  const { isReadOnly } = useReadOnlyMode();

  const methods = useForm({
    defaultValues: {
      description: description,
      files: existingFiles?.map((f) => f.id),
    },
    resolver: zodResolver(fileFormSchema),
  });

  const { formState, register, getValues, setValue, control, setError } =
    methods;

  const [currentFiles, setCurrentFiles] = useState(
    existingFiles?.map((x) => ({
      id: x.id ?? "",
      name: x.name ?? "",
    })) ?? []
  );

  // Watch form values for validation
  const watchedValues = useWatch({
    control,
    name: ["description", "files"],
  });

  // Query exhibit files
  const { data: exhibitFiles } = useExhibitFilesQuery({
    variables: {
      id: exhibit.exhibitId?.toString() ?? "",
    },
  });

  // Mutations for file operations
  const [unlinkExhibitFile] = useUnlinkExhibitFileMutation();
  const [deleteUserDocument] = useDeleteUserDocumentMutation();

  const handleSave = useCallback(
    async (data?: FileFormWithDescriptionType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          autoSave(data);
          setStatus("success");
        } catch (e) {
          setStatus("error");
          logError(e, { message: "Error saving description and files" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, logError]
  );

  const handleDescriptionSave = useCallback(
    async (description: string) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          autoSave({
            description,
            files: [],
          });
          setStatus("success");
        } catch (e) {
          setStatus("error");
          logError(e, { message: "Error saving description" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, logError]
  );

  const handleSubmit = useCallback(
    async (data?: FileFormWithDescriptionType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        onSubmit({
          description: data?.description || "",
          files: [],
        });
        setStatus("success");
      } catch (e) {
        setStatus("error");
        if (e instanceof Error) {
          setError("root.serverError", {
            type: e.name,
            message: e.message,
          });
        }
        logError(e, { message: "Error submitting description and files" });
      }
    },
    [onSubmit, setError, logError]
  );

  const debouncedDescriptionSave = useDebounceCallback(
    handleDescriptionSave,
    300
  );
  const isInitialMount = useRef(true);
  const previousDescription = useRef(watchedValues[0]);

  // Handle description changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousDescription.current = watchedValues[0];
      return;
    }

    // Only save if description has changed
    if (previousDescription.current !== watchedValues[0]) {
      previousDescription.current = watchedValues[0];
      debouncedDescriptionSave(watchedValues[0]);
    }
  }, [watchedValues, debouncedDescriptionSave]);

  // Handle file changes separately
  const onFileUpload = useCallback(
    async (file: File) => {
      try {
        setStatus("loading");
        const data = await api.files.uploadIndividualDoc(
          file,
          token,
          contact.id,
          fileType
        );

        if (!data.success || !data.id)
          throw new Error("An error occurred while uploading the file");

        const { id } = data;
        const files = getValues("files");
        const newFiles = [...files, id.toString()];

        setValue("files", newFiles, { shouldValidate: true });

        const newFile = {
          id: id.toString(),
          name: file.name,
        };

        setCurrentFiles((prev) => [...prev, newFile]);
        setStatus("success");

        // Save immediately after file upload
        handleSave({
          description: getValues("description"),
          files: newFiles,
        });

        return {
          success: data.success,
          name: file.name,
          id: id.toString(),
          link: "",
          fileId: id.toString(),
        };
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          contactId: contact.id,
          fileType,
        });
        return {
          success: false,
          name: file.name,
          type: file.type,
          link: "",
          id: "",
        };
      }
    },
    [contact.id, token, fileType, getValues, setValue, logError]
  );

  // Delete file handler
  const handleDelete = useCallback(
    async (file: UploadFileStatus) => {
      try {
        setStatus("loading");
        const exhibitFile =
          exhibitFiles?.exhibitById?.exhibitFilesByExhibitId?.nodes?.find(
            (f) => f?.fileByFileId?.id === file.id
          );

        // Unlink from exhibit if it exists
        if (exhibit.exhibitId && exhibitFile) {
          Sentry.addBreadcrumb({
            category: "exhibit",
            message: `Unlinking exhibit file ${file.id} from exhibit ${exhibit.exhibitId}`,
          });

          await unlinkExhibitFile({
            variables: {
              input: {
                clientMutationId: `unlink-exhibit-file-${exhibit.exhibitId}-${file.id}`,
                exhibitId: exhibit.exhibitId.toString(),
                fileId: file.id,
              },
            },
          });
        }

        // Delete the file
        if (file.id) {
          try {
            const { errors } = await deleteUserDocument({
              variables: {
                input: {
                  clientMutationId: `delete-user-document-${contact.id}-${file.id}`,
                  userId: contact.id,
                  fileId: file.id,
                },
              },
            });

            if (errors != null) {
              throw new Error(errors[0].message);
            }
          } catch (e) {
            if (e instanceof Error) {
              logError(e, { contactId: contact.id, fileId: file.id });
            } else if (e instanceof ApolloError) {
              logError(e.graphQLErrors[0], {
                contactId: contact.id,
                fileId: file.id,
              });
            } else {
              logError(e, { contactId: contact.id, fileId: file.id });
            }
            throw e;
          }
        }

        // Update files in form
        const files = getValues("files");
        const newFiles = files.filter((id) => id !== file.id);
        setValue("files", newFiles, { shouldValidate: true });

        setCurrentFiles((prev) => prev.filter((f) => f.id !== file.id));
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { contactId: contact.id, fileId: file.id });
        toast.error("An error occurred while deleting the file");
      }
    },
    [
      contact.id,
      exhibit.exhibitId,
      exhibitFiles,
      unlinkExhibitFile,
      deleteUserDocument,
      getValues,
      setValue,
      logError,
    ]
  );

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={title}
        subtitle={subtitle}
        description={descriptionProp}
        optional={!required}
        isSaving={isSaving}
        onSubmit={methods.handleSubmit(handleSubmit)}
        exhibit={exhibit}
        name={`${changeCase.kebabCase(fileType)}-form`}
        footnote={footnote}
        onBack={onBack}
        onSkipForNow={required ? onSkipForNow : onSkip}
        currentStep={currentStep}
        progress={moduleProgress}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
        isNextDisabled={!formState.isValid || status === "loading"}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem uploading your files or saving your description. Please try again."
        >
          <div>
            {existingFiles === null ? (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-accent rounded-full border-t-transparent"></div>
              </div>
            ) : (
              <Uploader
                multiple
                onFileUpload={onFileUpload}
                defaultFiles={currentFiles}
                disabled={isReadOnly}
                allowDelete
                onDeleteFile={handleDelete}
              />
            )}
          </div>

          <OnboardingFormField
            name="description"
            label={descriptionLabel}
            type="textarea"
            placeholder="Enter text here..."
            required={true}
            disabled={isReadOnly}
          />
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const FileWithLinksFormController: React.FC<
  OnboardingPageType & {
    fileType: IndividualDocumentType;
    title: string;
    subtitle: React.ReactNode;
    cardTitle: React.ReactNode;
    descriptionLabel: React.ReactNode;
  }
> = (props) => {
  const { exhibit, module, contact, fileType } = props;
  const { data: userData, loading } = useAllUserDocumentsByTypeQuery({
    variables: {
      userId: contact.id,
      documentType: fileType,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  if (loading) {
    return <WaitingSplash />;
  }

  return (
    <FileWithLinksForm
      {...props}
      userDocuments={userData?.allUserDocuments?.nodes?.filter((n) => !!n)}
    />
  );
};

export const AwardDocumentsForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { data, name, description, award } = exhibit as WorkflowAwardExhibit;
  const { name: moduleName } = module;
  return (
    <FileWithLinksFormController
      {...props}
      subtitle={name}
      cardTitle={
        "Please upload evidence of your receipt of the award below. This could be a formal email, letter, weblink showing your acceptance, or a photo of your award. "
      }
      title={`${moduleName}${award ? ` - ${award.awardName}` : ""}`}
      fileType={IndividualDocumentType.AwardEvidence}
      descriptionLabel={`Can you tell us about the award and why it's a big deal in your field?`}
      footnote={ScreenshotFootnote}
    />
  );
};
